<template>
  <div
    :class="[
      'grid h-full min-h-0 min-w-0 w-full grid-cols-1 items-stretch gap-4 overflow-hidden lg:overflow-visible',
      showIaCards
        ? 'sm:grid-cols-[minmax(0,3fr)_250px] lg:grid-cols-[minmax(0,3fr)_300px]  2xl:grid-cols-[minmax(0,4fr)_380px]'
        : '',
    ]"
  >
    <div class="h-full min-h-0 min-w-0">
      <Whatsapp />
    </div>

    <aside v-if="showIaCards" class="h-full min-h-0 min-w-0 overflow-visible lg:sticky lg:top-0">
      <AiCardsPanel
        class="whatsapp-ai-cards-panel"
        :cards="whatsappIaCards"
        :state="aiPanelState"
        :state-message="aiPanelMessage"
        storage-key="whatsapp-ia-cards-layout"
        :show-refresh="aiPanelState === 'disabled'"
        :on-refresh="refreshSelectedCampaignIaStatus"
        disable-transcript
        v-model:insights-tab="activeInsightsTab"
      />
    </aside>
  </div>

  <KnowledgePreviewDialog
    ref="knowledgePreviewDialog"
    v-model="knowledgePreviewVisible"
    :campaign-id="selectedCampaignId"
  />

  <KnowledgeChatDialog
    ref="knowledgeChatDialog"
    v-model="knowledgeChatVisible"
    :campaign-id="selectedCampaignId"
    context="whatsapp"
    :ready-documents-count="readyDocuments.length"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { BookOpenText } from 'lucide-vue-next'

import IconIaSummary from '@/assets/svg/iaSummaryIcon.svg?component'
import IconSuggestions from '@/assets/svg/suggestionsIcon.svg?component'
import KnowledgeChatDialog from '@/components/molecules/ai/KnowledgeChatDialog.vue'
import KnowledgePreviewDialog from '@/components/molecules/ai/KnowledgePreviewDialog.vue'
import AiCardsPanel from '@/components/organism/ia/AiCardsPanel.vue'
import Whatsapp from '@/components/whatsapp/WhatsApp.vue'
import { useChatStore } from '@/components/whatsapp/stores/useChatStore'
import { useWhatsAppCoordinator } from '@/components/whatsapp/stores/useWhatsAppCoordinator'
import { useCampaignKnowledge, KNOWLEDGE_STATUS_COLOR_MAP, KNOWLEDGE_STATUS_LABEL_MAP } from '@/composables/ai/useCampaignKnowledge'
import { useIaCardsVisibility } from '@/composables/useIaCardsVisibility'
import type { ICampaignKnowledgeDocument } from '@/services/campaign/useCampaignService'
import { useCampaignService } from '@/services/campaign/useCampaignService'
import type { AiCard, AiPanelState } from '@/components/organism/ia/types'
import { useWhatsappAiStore } from '@/stores/useWhatsappAiStore'
import { useWorkspaceStore } from '@/stores/useWorkspaceStore'
import { useI18n } from 'vue-i18n'

const chatStore = useChatStore()
const whatsAppCoordinator = useWhatsAppCoordinator()
const whatsappAiStore = useWhatsappAiStore()
const workspace = useWorkspaceStore()
const campaignService = useCampaignService()
const { t } = useI18n()
const { isIaCardsVisible } = useIaCardsVisibility()
const {
  cardBody: knowledgeCardBody,
  documents: knowledgeDocuments,
  loadDocuments: fetchKnowledgeDocuments,
  loading: knowledgeLoading,
  readyDocuments,
  reset: resetKnowledgeDocuments,
} = useCampaignKnowledge()

const showIaCards = computed(() => isIaCardsVisible.value)
const activeInsightsTab = ref<'summary' | 'suggestions'>('summary')
const knowledgePreviewVisible = ref(false)
const knowledgeChatVisible = ref(false)
const knowledgeChatDialog = ref<InstanceType<typeof KnowledgeChatDialog> | null>(null)
const knowledgePreviewDialog = ref<InstanceType<typeof KnowledgePreviewDialog> | null>(null)

const importantInsightEvents = [
  'CHAT_FINISHED',
  'TRANSFER_ACCEPTED',
  'CHAT_TRANSFERRED_TO_CAMPAIGN',
  'CHAT_TRANSFERRED_TO_BOT',
  'CHAT_CLOSED_BY_TIMEOUT_CLIENT',
  'CHAT_RELEASED_BY_USER_TIMEOUT',
  'CHAT_RELEASED_BY_USER_LOGOUT',
  'CHAT_EXPIRED_BY_META_WINDOW',
]

const activeInsight = computed(() => {
  const chatId = chatStore.selectedChatId
  return chatId ? whatsappAiStore.stateFor(chatId) : null
})

const selectedChatSnapshot = computed(() => ({
  chatId: chatStore.selectedChat?.chat_id ?? null,
  messageCount: chatStore.selectedChat?.messages.length ?? 0,
}))

const selectedCampaignId = computed(() => chatStore.selectedChat?.campaign_id ?? null)

const emptyStateMessage = computed(() => {
  if (!chatStore.selectedChatId) return t('ai.whatsapp_page.select_chat_for_insights')
  return activeInsight.value?.error || t('ai.whatsapp_page.waiting_for_context')
})

const knowledgeCardItems = computed(() =>
  knowledgeDocuments.value.map((document) => ({
    id: document.id,
    label: document.title,
    status:
      KNOWLEDGE_STATUS_LABEL_MAP[document.status as keyof typeof KNOWLEDGE_STATUS_LABEL_MAP] ??
      document.status,
    statusSeverity:
      KNOWLEDGE_STATUS_COLOR_MAP[document.status as keyof typeof KNOWLEDGE_STATUS_COLOR_MAP],
    onClick: () => openKnowledgeDocumentPreview(document),
  })),
)

const selectedCampaignUsesIa = computed(() =>
  selectedCampaignId.value ? workspace.getCampaignUseIa(selectedCampaignId.value) : false,
)
const canGenerateWhatsappInsights = computed(
  () =>
    showIaCards.value &&
    Boolean(chatStore.selectedChatId) &&
    selectedCampaignUsesIa.value,
)

const aiPanelState = computed<AiPanelState>(() => {
  if (!chatStore.selectedChatId) return 'empty'
  if (!selectedCampaignId.value) return 'empty'
  if (!selectedCampaignUsesIa.value) return 'disabled'
  return 'ready'
})

const aiPanelMessage = computed(() => {
  if (!chatStore.selectedChatId) return t('ai.whatsapp_page.select_chat_for_ai')
  if (!selectedCampaignId.value) return t('ai.whatsapp_page.chat_without_campaign')
  if (!selectedCampaignUsesIa.value) return t('ai.whatsapp_page.ai_disabled_for_campaign')
  return undefined
})

const whatsappIaCards = computed<AiCard[]>(() => [
  {
    id: 'ia-suggestions',
    title: t('ai.whatsapp_page.ai_suggestions'),
    body: activeInsight.value?.suggestions.length
      ? activeInsight.value.suggestions
      : [emptyStateMessage.value],
    icon: IconSuggestions,
    loading: activeInsight.value?.loading ?? false,
  },
  {
    id: 'ia-summary',
    title: t('ai.whatsapp_page.ai_chat_summary'),
    body: activeInsight.value?.summary ? [activeInsight.value.summary] : [emptyStateMessage.value],
    icon: IconIaSummary,
    loading: activeInsight.value?.loading ?? false,
  },
  {
    id: 'ia-knowledge-base',
    title: t('ai.whatsapp_page.knowledge_base'),
    body: knowledgeCardBody.value,
    items: knowledgeCardItems.value,
    chat: {
      placeholder: t('ai.whatsapp_page.ask_knowledge_base'),
      disabled: !readyDocuments.value.length,
      openLabel: t('ai.whatsapp_page.view_chat'),
      onOpen: openKnowledgeChat,
      onSubmit: askKnowledgeFromCard,
    },
    icon: BookOpenText,
    loading: knowledgeLoading.value,
  },
])

const refreshSelectedCampaignIaStatus = async () => {
  const campaignId = selectedCampaignId.value
  if (!campaignId) return

  const campaign = await campaignService.getCampaignById(campaignId)
  const useAi = workspace.getCampaignUseAi(campaign)
  workspace.setCampaignUseIa(campaignId, useAi)
  workspace.setUseIa(useAi)
}

const openKnowledgeChat = () => {
  knowledgeChatVisible.value = true
}

const askKnowledgeFromCard = async (question: string) => {
  await knowledgeChatDialog.value?.sendQuestion(question)
}

const openKnowledgeDocumentPreview = (document: ICampaignKnowledgeDocument) => {
  knowledgePreviewVisible.value = true
  knowledgePreviewDialog.value?.openDocument(document)
}

watch(
  selectedChatSnapshot,
  ({ chatId, messageCount }, previous) => {
    if (previous?.chatId && previous.chatId !== chatId) {
      whatsappAiStore.cancelScheduledInsights(previous.chatId)
    }
    if (!chatId || !canGenerateWhatsappInsights.value) return
    const state = whatsappAiStore.stateFor(chatId)
    if (state.hydrated) {
      whatsappAiStore.scheduleInsights(chatId, messageCount)
      return
    }
    void whatsappAiStore.refreshInsights(chatId, messageCount)
  },
  { immediate: true },
)

watch(
  selectedCampaignId,
  (campaignId) => {
    if (!campaignId) {
      resetKnowledgeDocuments()
      return
    }

    void refreshSelectedCampaignIaStatus()
    void fetchKnowledgeDocuments(campaignId)
  },
  { immediate: true },
)

const handleImportantInsightEvent = (eventType: string) => (data: unknown) => {
  if (!canGenerateWhatsappInsights.value) return
  if (!data || typeof data !== 'object' || !('chat_id' in data)) return
  const chatId = String((data as { chat_id?: unknown }).chat_id || '')
  if (!chatId || chatId !== chatStore.selectedChatId) return
  const chat = chatStore.chats.find((item) => item.chat_id === chatId)
  whatsappAiStore.scheduleInsights(chatId, chat?.messages.length ?? 0, eventType)
}

watch(canGenerateWhatsappInsights, (enabled) => {
  if (!enabled) {
    if (chatStore.selectedChatId) {
      whatsappAiStore.cancelScheduledInsights(chatStore.selectedChatId)
    }
    return
  }
  if (!chatStore.selectedChatId) return
  const chat = chatStore.selectedChat
  void whatsappAiStore.refreshInsights(
    chatStore.selectedChatId,
    chat?.messages.length ?? 0,
  )
})

const importantEventHandlers = importantInsightEvents.map((eventType) => ({
  eventType,
  handler: handleImportantInsightEvent(eventType),
}))

onMounted(() => {
  importantEventHandlers.forEach(({ eventType, handler }) => {
    whatsAppCoordinator.webSocketStore.on(eventType, handler)
  })
})

onUnmounted(() => {
  importantEventHandlers.forEach(({ eventType, handler }) => {
    whatsAppCoordinator.webSocketStore.off(eventType, handler)
  })
  whatsappAiStore.dispose()
})
</script>

<style scoped>
.whatsapp-ai-cards-panel :deep(> div) {
  min-height: 0;
}
</style>
